import React, { useCallback, useEffect, useState } from "react";
import { getToken, getMe, logout, setOnAuthError } from "./api";
import { errMsg } from "./utils";
import { Login, ChangePassword } from "./views/Login";
import Home from "./views/Home";
import CrearCupon from "./views/CrearCupon";
import Voucher from "./views/Voucher";
import Cupones from "./views/Cupones";
import Movimientos from "./views/Movimientos";

const LOGO = process.env.PUBLIC_URL + "/fs-black.png";

/* Íconos de línea (mismo estilo que el menú de taquilla-fe). */
const IconInicio = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 10.7 12 4l9 6.7" /><path d="M5.5 9.5V20h13V9.5" /><path d="M10 20v-5h4v5" />
  </svg>
);
const IconCupones = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2 2 2 0 0 0 0 4 2 2 0 0 1-2 2H6a2 2 0 0 1-2-2 2 2 0 0 0 0-4Z" />
    <path d="M14.5 6.5v11" strokeDasharray="2 2.4" />
  </svg>
);
const IconMovs = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5.5 4h9L19 8.5V19a1 1 0 0 1-1 1H5.5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
    <path d="M8 10.5h7M8 14h7M8 17h4" />
  </svg>
);

const NAV = [
  { key: "home", label: "Inicio", icon: <IconInicio /> },
  { key: "list", label: "Mis cupones", icon: <IconCupones /> },
  { key: "movs", label: "Movimientos", icon: <IconMovs /> },
];

const TITLES = {
  home: "Inicio",
  create: "Crear cupón",
  voucher: "Cupón de reserva",
  list: "Mis cupones",
  movs: "Movimientos del cupo",
};

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "P";
  return (parts[0][0] + (parts[1] ? parts[1][0] : "")).toUpperCase();
}

/**
 * Portal de Proveedores de ALAS. Mismo shell que taquilla-fe: barra lateral
 * fija en escritorio, header pegajoso y tabbar en móvil.
 * Pantallas: boot | login | change | home | create | voucher | list | movs
 */
export default function App() {
  const [screen, setScreen] = useState("boot");
  const [me, setMe] = useState(null);
  const [meError, setMeError] = useState("");
  const [loginInfo, setLoginInfo] = useState("");
  const [chg, setChg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [voucherBack, setVoucherBack] = useState("home");
  const [toast, setToast] = useState("");

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(""), 3200);
  }

  const loadHome = useCallback(async () => {
    setScreen("boot");
    setMeError("");
    try {
      setMe(await getMe());
      setScreen("home");
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        setScreen("login");
      } else {
        setMe(null);
        setMeError(errMsg(e, "No se pudo cargar tu cuenta. Intenta de nuevo."));
        setScreen("home");
      }
    }
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      setMe(await getMe());
    } catch {
      /* silencioso */
    }
  }, []);

  useEffect(() => {
    setOnAuthError(() => {
      setMe(null);
      setLoginInfo("Tu sesión expiró o fue iniciada en otro dispositivo. Ingresa de nuevo.");
      setScreen("login");
    });
    if (getToken()) loadHome();
    else setScreen("login");
  }, [loadHome]);

  function handleLogout() {
    logout();
    setMe(null);
    setDraft(null);
    setVoucher(null);
    setLoginInfo("");
    setScreen("login");
  }

  // ---------- Pantallas sin sesión ----------
  if (screen === "boot") {
    return (
      <div className="center" style={{ marginTop: "42vh" }}>
        <div className="spinner" />
      </div>
    );
  }

  if (screen === "login") {
    return (
      <Login
        info={loginInfo}
        onDone={() => {
          setLoginInfo("");
          loadHome();
        }}
        onMustChange={(email, currentPassword) => {
          setChg({ email, currentPassword });
          setScreen("change");
        }}
      />
    );
  }

  if (screen === "change") {
    return (
      <ChangePassword
        email={chg?.email}
        currentPassword={chg?.currentPassword}
        onDone={() => {
          setChg(null);
          setLoginInfo("");
          loadHome();
        }}
        onBackToLogin={(info) => {
          setChg(null);
          setLoginInfo(info || "");
          setScreen("login");
        }}
      />
    );
  }

  // ---------- Pantallas con sesión ----------
  // El nombre limpio del convenio; company_name viene como slug del maestro.
  const companyName = me?.contract_name || me?.company_name || "Proveedor";
  const isAdmin = me?.role === "admin";
  // Los usuarios operativos no ven Movimientos (la plata es solo del admin).
  const navItems = isAdmin ? NAV : NAV.filter((n) => n.key !== "movs");
  const showNav = navItems.some((n) => n.key === screen);

  return (
    <div className="prov-shell">
      {/* ---------- Barra lateral (escritorio) ---------- */}
      <aside className="prov-sidebar no-print">
        <div className="brand">
          <span className="brand-logo"><img src={LOGO} alt="ALAS" /></span>
          <div className="brand-text">
            <span className="brand-name">ALAS</span>
            <span className="brand-tag">Proveedores</span>
          </div>
        </div>

        <nav className="side-menu">
          <div className="menu-eyebrow">Menú</div>
          {navItems.map((n) => (
            <button
              key={n.key}
              className={`side-link ${screen === n.key ? "active" : ""}`}
              onClick={() => setScreen(n.key)}
            >
              <span className="side-ico">{n.icon}</span>
              {n.label}
            </button>
          ))}
        </nav>

        <div className="side-footer">
          <div className="user-card">
            <span className="user-avatar">{initials(companyName)}</span>
            <div className="user-meta">
              <span className="user-name">{companyName}</span>
              <span className="user-sub">{isAdmin ? "Administrador" : "Usuario"}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* ---------- Main ---------- */}
      <div className="prov-main">
        <header className="prov-header no-print">
          <div className="ph-left">
            <span className="ph-brand-mobile"><img src={LOGO} alt="ALAS" /></span>
            <div className="ph-brand-mobile-text">
              <span className="n">ALAS</span>
              <span className="t">Proveedores</span>
            </div>
            <h1 className="ph-title">{TITLES[screen] || "Portal de Proveedores"}</h1>
          </div>
          <div className="ph-right">
            <div className="ph-company">
              <span className="ph-company-lbl">Empresa</span>
              <span className="ph-company-name">{companyName}</span>
            </div>
            <button className="ph-logout" onClick={handleLogout}>Salir</button>
          </div>
        </header>

        <main className="prov-content">
          {screen === "home" && (
            <Home
              me={me}
              meError={meError}
              onRetryMe={loadHome}
              onCreate={(d) => {
                setDraft(d);
                setScreen("create");
              }}
            />
          )}

          {screen === "create" && draft && (
            <CrearCupon
              draft={draft}
              onCancel={() => setScreen("home")}
              onCreated={(reservation) => {
                setVoucher(reservation);
                setVoucherBack("home");
                refreshMe();
                setScreen("voucher");
                showToast("Cupón creado.");
              }}
            />
          )}

          {screen === "voucher" && (
            <Voucher
              reservation={voucher}
              me={me}
              onToast={showToast}
              onBack={() => setScreen(voucherBack === "list" ? "list" : "home")}
            />
          )}

          {screen === "list" && (
            <Cupones
              onShowVoucher={(r) => {
                setVoucher(r);
                setVoucherBack("list");
                setScreen("voucher");
              }}
              onBalanceChange={refreshMe}
              onToast={showToast}
            />
          )}

          {screen === "movs" && <Movimientos me={me} />}
        </main>
      </div>

      {/* ---------- Tabbar inferior (móvil) ---------- */}
      {showNav && (
        <nav className="prov-tabbar no-print">
          {navItems.map((n) => (
            <button
              key={n.key}
              className={screen === n.key ? "on" : ""}
              onClick={() => setScreen(n.key)}
            >
              <span className="tico">{n.icon}</span>
              {n.label}
            </button>
          ))}
        </nav>
      )}

      {toast && <div className="toast no-print">{toast}</div>}
    </div>
  );
}
